import { create } from 'zustand';
import { ListHeroImages, CreateHeroImage, GetHeroImage, UpdateHeroImage, DeleteHeroImage, CreateHeroImageI, UpdateHeroImageI } from '../api/api';
import toast from 'react-hot-toast';

export interface HeroImage {
  _id: string;
  image: string;
  order: number;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetHeroImagesResponse {
  data: {
    heroImages: HeroImage[];
    pagination?: PaginationInfo;
  };
}

interface HeroImageResponse {
  data: {
    heroImage: HeroImage;
  };
  status: boolean;
  message: string;
}

interface DeleteHeroImageResponse {
  status: boolean;
  message: string;
}

interface HeroImageStore {
  heroImages: HeroImage[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getHeroImages: (activeOnly?: boolean, isPublic?: boolean, search?: string, page?: number, limit?: number) => Promise<void>;
  createHeroImage: (heroImage: CreateHeroImageI) => Promise<void>;
  getHeroImage: (id: string) => Promise<HeroImage | null>;
  updateHeroImage: (id: string, heroImage: UpdateHeroImageI) => Promise<void>;
  deleteHeroImage: (id: string) => Promise<void>;
}

export const useHeroImageStore = create<HeroImageStore>((set, get) => ({
  heroImages: [] as HeroImage[],
  loading: false,
  error: null,
  pagination: null,

  getHeroImages: async (activeOnly?: boolean, isPublic?: boolean, search?: string, page?: number, limit?: number) => {
    set({ loading: true, error: null });
    const { data, error } = await ListHeroImages<GetHeroImagesResponse>(activeOnly, isPublic, search, page, limit);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      heroImages: data.data.heroImages || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createHeroImage: async (heroImage: CreateHeroImageI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateHeroImage<HeroImageResponse>(heroImage);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create hero image' });
      return;
    }

    const heroImages = get().heroImages;
    set({ heroImages: [...heroImages, data.data.heroImage], loading: false });
    toast.success('Hero image created successfully');
  },

  getHeroImage: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetHeroImage<HeroImageResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get hero image' });
      return null;
    }

    set({ loading: false });
    return data.data.heroImage;
  },

  updateHeroImage: async (id: string, heroImage: UpdateHeroImageI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateHeroImage<HeroImageResponse>(id, heroImage);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update hero image' });
      return;
    }

    const heroImages = get().heroImages;
    const updatedHeroImages = heroImages.map((h) => (h._id === id ? { ...h, ...data.data.heroImage } : h));
    set({ heroImages: updatedHeroImages, loading: false });
    toast.success('Hero image updated successfully');
  },

  deleteHeroImage: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteHeroImage<DeleteHeroImageResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete hero image' });
      return;
    }

    const heroImages = get().heroImages.filter((heroImage) => heroImage._id !== id);
    set({ heroImages, loading: false });
    toast.success('Hero image deleted successfully');
  },
}));
